/**
 * Service Registry - Handles registration of all services in the container
 * Extracted from main.ts to follow Single Responsibility Principle
 */

import type { StateManagerInterface, DataManagerInterface, ConfigManagerInterface, AudioEngineInterface, EQExporterInterface, NotificationSystemInterface, UIControllerInterface, EQProcessorInterface } from '../types';
import { container, ServiceKeys } from './service-container';
import { Logger } from './logger';
import * as MathUtils from './math-utils';

// Import data management
import { StateManager } from '../data/state-manager';
import { DataManager } from '../data/data-manager';
import { ConfigManager } from '../data/config-manager';

// Import core services
import { Environment } from './environment';
import { AudioEngine } from './audio-engine';
import { EQProcessor } from './eq-processor';

// Import UI components
import { UIController } from '../features/equalizer/ui-controller';
import { NotificationSystem } from '../features/equalizer/notification-system';
import { ModalDialog } from '../features/equalizer/modal-dialog';
import { createReactGraphAdapter } from '../ui/react/mount-graph';

// Import export functionality
import { EQExporter } from '../export/eq-exporter';

const logger = Logger.createComponentLogger('service-registry');

export class ServiceRegistry {
    /**
     * Register all services in the dependency injection container
     */
    public registerAll(): void {
        logger.info('Registering all services in container...');
        
        this.registerCoreServices();
        this.registerDataServices();
        this.registerProcessingServices();
        this.registerUIServices();
        this.registerExportServices();
        
        logger.info('All services registered successfully');
    }

    /**
     * Register core framework services
     */
    private registerCoreServices(): void {
        container.register(ServiceKeys.ENVIRONMENT, Environment);
        container.register(ServiceKeys.LOGGER, Logger);
        container.register(ServiceKeys.MATH_UTILS, MathUtils);
    }

    /**
     * Register data management services with proper dependency order
     */
    private registerDataServices(): void {
        // DataManager first, then ConfigManager with dependency
        const dataManager = new DataManager();
        const stateManager = new StateManager();
        const configManager = new ConfigManager(dataManager);
        
        container.register(ServiceKeys.STATE_MANAGER, stateManager);
        container.register(ServiceKeys.DATA_MANAGER, dataManager);
        container.register(ServiceKeys.CONFIG_MANAGER, configManager);
    }

    /**
     * Register core audio and EQ processing services
     */
    private registerProcessingServices(): void {
        const audioEngine = new AudioEngine();
        const eqProcessor = new EQProcessor();
        
        container.register(ServiceKeys.AUDIO_ENGINE, audioEngine);
        container.register(ServiceKeys.EQ_PROCESSOR, eqProcessor);
    }

    /**
     * Register UI services with their dependencies
     */
    private registerUIServices(): void {
        const notificationSystem = new NotificationSystem();
        const modalDialog = new ModalDialog();
        const graphRenderer = createReactGraphAdapter('hearing-graph');
        
        // UIController needs dependencies from container
        const audioEngine = container.get<AudioEngineInterface>(ServiceKeys.AUDIO_ENGINE);
        const eqProcessor = container.get<EQProcessorInterface>(ServiceKeys.EQ_PROCESSOR);
        const dataManager = container.get<DataManagerInterface>(ServiceKeys.DATA_MANAGER);
        const configManager = container.get<ConfigManagerInterface>(ServiceKeys.CONFIG_MANAGER);
        
        const uiController = new UIController(audioEngine, eqProcessor, dataManager, configManager);
        
        container.register(ServiceKeys.NOTIFICATION_SYSTEM, notificationSystem);
        container.register(ServiceKeys.MODAL_DIALOG, modalDialog);
        container.register(ServiceKeys.GRAPH_RENDERER, graphRenderer);
        container.register(ServiceKeys.UI_CONTROLLER, uiController);
    }

    /**
     * Register export and utility services
     */
    private registerExportServices(): void {
        const eqProcessor = container.get<EQProcessorInterface>(ServiceKeys.EQ_PROCESSOR);
        const eqExporter = new EQExporter(eqProcessor);
        
        container.register(ServiceKeys.EQ_EXPORTER, eqExporter);
    }
}